import { SubmitAttemptRequestSchema, type SubmitAttemptRequest } from "@algoquest/contracts";
import type { LevelDefinition, AlgorithmOperation } from "@algoquest/schemas";

/**
 * Phase 10 (Master Spec §14/§19/§26/§32) — the game client's local
 * persistence layer: a pending-attempt outbox, a cached copy of the
 * user's level/progress list for offline viewing, and (the outbox
 * itself doubling as) a retry queue. IndexedDB is never the authoritative
 * store — PostgreSQL is (§9/§32) — this is purely resilience so a
 * completed level is never lost to a dropped connection or a closed tab.
 *
 * A hand-written raw `indexedDB` wrapper, not a third-party library (e.g.
 * `idb`): this environment cannot `npm install` to verify any such
 * library's exact API surface (the same reasoning that drove Phase 09's
 * WebFetch verification of `@fastify/jwt`'s real API before using it),
 * whereas the native `indexedDB` API's semantics are stable and
 * well-specified enough to reason about directly without that risk.
 *
 * Graceful degrade (§26 risk #5, "needs a graceful degrade path ... to
 * be designed in Phase 10"): if `indexedDB` is unavailable or `open()`
 * fails (some private-browsing modes), every function below transparently
 * falls back to an in-memory `Map`-backed store for the rest of the tab's
 * lifetime. `isPersistenceDegraded()` reports this so `appStore`/the UI
 * can warn the player, and a `beforeunload` confirmation is installed
 * the first time the in-memory outbox becomes non-empty — the risk
 * note's own "sync-on-close-tab-warning" language.
 */

let dbName = "algoquest-3d";

export interface CampaignAttempt extends SubmitAttemptRequest { levelDefinition: LevelDefinition }
const DB_VERSION = 1;
const STORE_OUTBOX_ATTEMPTS = "outboxAttempts";
const STORE_OUTBOX_EVENTS = "outboxEvents";
const STORE_PROGRESS_CACHE = "progressCache";

export interface OutboxEventsRecord {
  attemptId: string;
  operations: AlgorithmOperation[];
}

export interface ProgressCacheRecord {
  globalLevelNumber: number;
  completed: boolean;
  mastered: boolean;
  bestStars?: number;
  bestDurationMs: number | null;
  attemptsCount: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;
let usingFallback = false;

const fallbackOutboxAttempts = new Map<string, CampaignAttempt>();
const fallbackOutboxEvents = new Map<string, OutboxEventsRecord>();
const fallbackProgressCache = new Map<number, ProgressCacheRecord>();

function getDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      usingFallback = true;
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(dbName, DB_VERSION);
    } catch {
      usingFallback = true;
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_OUTBOX_ATTEMPTS)) {
        db.createObjectStore(STORE_OUTBOX_ATTEMPTS, { keyPath: "attemptId" });
      }
      if (!db.objectStoreNames.contains(STORE_OUTBOX_EVENTS)) {
        db.createObjectStore(STORE_OUTBOX_EVENTS, { keyPath: "attemptId" });
      }
      if (!db.objectStoreNames.contains(STORE_PROGRESS_CACHE)) {
        db.createObjectStore(STORE_PROGRESS_CACHE, { keyPath: "globalLevelNumber" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      usingFallback = true;
      resolve(null);
    };
  });
  return dbPromise;
}

export function isPersistenceDegraded(): boolean {
  return usingFallback;
}

let beforeUnloadWarningInstalled = false;
function warnBeforeUnloadIfPending(): void {
  if (beforeUnloadWarningInstalled || typeof window === "undefined") return;
  beforeUnloadWarningInstalled = true;
  window.addEventListener("beforeunload", (event) => {
    if (fallbackOutboxAttempts.size > 0 || fallbackOutboxEvents.size > 0) {
      event.preventDefault();
      event.returnValue = "";
    }
  });
}

/**
 * Enqueues one completed play-through's attempt summary and its full
 * operation trace together, in a single IndexedDB transaction spanning
 * both object stores (or, in the fallback path, both `Map`s updated
 * together) — a completed attempt and its trace are one atomic unit from
 * the local persistence layer's point of view, even though the server
 * accepts them via two separate requests (see `attempt-events.ts`'s doc
 * comment in `@algoquest/contracts` for why they're split server-side).
 *
 * `SubmitAttemptRequestSchema.parse` runs here even though `attempt` is
 * already TypeScript-typed as `SubmitAttemptRequest` — a local IndexedDB
 * write is still a trust boundary in the §10 sense (something durable is
 * being persisted), and it is far cheaper to catch a malformed record
 * here, loudly, than to silently store it and have `POST /attempts`
 * reject it much later during sync.
 */
export async function enqueueCompletedAttempt(attempt: SubmitAttemptRequest, operations: AlgorithmOperation[], levelDefinition: LevelDefinition): Promise<void> {
  SubmitAttemptRequestSchema.parse(attempt);
  const record: CampaignAttempt = { ...attempt, levelDefinition };
  const db = await getDb();
  if (!db) {
    fallbackOutboxAttempts.set(attempt.attemptId, record);
    fallbackOutboxEvents.set(attempt.attemptId, { attemptId: attempt.attemptId, operations });
    warnBeforeUnloadIfPending();
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([STORE_OUTBOX_ATTEMPTS, STORE_OUTBOX_EVENTS], "readwrite");
    tx.objectStore(STORE_OUTBOX_ATTEMPTS).put(record);
    tx.objectStore(STORE_OUTBOX_EVENTS).put({ attemptId: attempt.attemptId, operations });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function listOutboxAttempts(): Promise<CampaignAttempt[]> {
  const db = await getDb();
  if (!db) return Array.from(fallbackOutboxAttempts.values());
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_OUTBOX_ATTEMPTS, "readonly");
    const req = tx.objectStore(STORE_OUTBOX_ATTEMPTS).getAll();
    req.onsuccess = () => resolve(req.result as CampaignAttempt[]);
    req.onerror = () => reject(req.error);
  });
}

export async function listOutboxEvents(): Promise<OutboxEventsRecord[]> {
  const db = await getDb();
  if (!db) return Array.from(fallbackOutboxEvents.values());
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_OUTBOX_EVENTS, "readonly");
    const req = tx.objectStore(STORE_OUTBOX_EVENTS).getAll();
    req.onsuccess = () => resolve(req.result as OutboxEventsRecord[]);
    req.onerror = () => reject(req.error);
  });
}

export async function removeOutboxAttempt(attemptId: string): Promise<void> {
  const db = await getDb();
  if (!db) {
    fallbackOutboxAttempts.delete(attemptId);
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_OUTBOX_ATTEMPTS, "readwrite");
    tx.objectStore(STORE_OUTBOX_ATTEMPTS).delete(attemptId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function removeOutboxEvents(attemptId: string): Promise<void> {
  const db = await getDb();
  if (!db) {
    fallbackOutboxEvents.delete(attemptId);
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_OUTBOX_EVENTS, "readwrite");
    tx.objectStore(STORE_OUTBOX_EVENTS).delete(attemptId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Wholesale-replaces the cached progress list with the server's latest — called once per successful `GET /progress` (see `syncClient.ts`), never merged incrementally, since the server is always the authoritative source (§19). */
export async function cacheProgress(rows: ProgressCacheRecord[]): Promise<void> {
  const db = await getDb();
  if (!db) {
    fallbackProgressCache.clear();
    for (const row of rows) fallbackProgressCache.set(row.globalLevelNumber, row);
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_PROGRESS_CACHE, "readwrite");
    const store = tx.objectStore(STORE_PROGRESS_CACHE);
    store.clear();
    for (const row of rows) store.put(row);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getCachedProgress(): Promise<ProgressCacheRecord[]> {
  const db = await getDb();
  if (!db) return Array.from(fallbackProgressCache.values());
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PROGRESS_CACHE, "readonly");
    const req = tx.objectStore(STORE_PROGRESS_CACHE).getAll();
    req.onsuccess = () => resolve(req.result as ProgressCacheRecord[]);
    req.onerror = () => reject(req.error);
  });
}

/** Separate users and canonical campaign attempts from the old prototype outbox.
 * Legacy data remains untouched: its level numbers may refer to different puzzles.
 */
export function configureCampaignStorage(userId: string): void {
  const nextName = `algoquest-campaign-${userId}`;
  if (dbName === nextName) return;
  void dbPromise?.then(db => db?.close());
  dbName = nextName;
  dbPromise = null;
  usingFallback = false;
  fallbackOutboxAttempts.clear();
  fallbackOutboxEvents.clear();
  fallbackProgressCache.clear();
}

/** Persist the server acknowledgement before removing its outbox entry. If the
 * later trace upload fails, this completed level still survives an offline reload.
 */
export async function cacheProgressRow(row: ProgressCacheRecord): Promise<void> {
  const db = await getDb();
  if (!db) { fallbackProgressCache.set(row.globalLevelNumber, row); return; }
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_PROGRESS_CACHE, 'readwrite');
    tx.objectStore(STORE_PROGRESS_CACHE).put(row);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
